// TelcoTrack — outgoing primary záfuk cables at the source cabinet.
// Destination cabinets store the optical záfuk; the source must show the same fibers leaving.

import {
  fiberCountFromTypOk,
  outgoingWeldCableId,
  peerLookupCablesPointingAtSource,
  type RozvadecWeldCable,
  type RozvadecWeldCableLookup,
} from './rozvadec-zafuk.constants';
import {
  applyDerivedTypOkList,
  typOkFromMaterial,
  type DerivedTypOkValue,
  type OkMaterialUsageLike,
} from './typ-ok-from-material.constants';
import type { RozvadecWeldLayoutCable } from './rozvadec-weld-layout.types';

/** Dest token for origin záfuk listed at this cabinet (no peer dest yet). */
export const LOCAL_ORIGIN_WELD_DEST_ID = 'local';

export type RozvadecOutgoingPeerLookup = {
  destRozvadecObjectId: string;
  destRozvadecName?: string | null;
  lookup: RozvadecWeldCableLookup;
};

/**
 * Mirror peer incoming optical záfuk (where this cabinet is „zafouknuto z“)
 * as outgoing primary cables. Skips microtubes and origin-only rows already
 * omitted by `inspectRozvadecWeldCablesFromZafuk`.
 */
export function outgoingCablesFromPeerLookups(
  sourceCabinetId: string,
  peers: readonly RozvadecOutgoingPeerLookup[],
): RozvadecWeldLayoutCable[] {
  const source = sourceCabinetId.trim();
  if (!source) return [];
  const out: RozvadecWeldLayoutCable[] = [];
  const seen = new Set<string>();
  for (const peer of peers) {
    const destId = peer.destRozvadecObjectId.trim();
    if (!destId || destId === source) continue;
    const destName = (peer.destRozvadecName ?? '').trim() || null;
    for (const cable of peerLookupCablesPointingAtSource(peer.lookup, source)) {
      const cableId = outgoingWeldCableId(destId, cable.id);
      if (!cableId || seen.has(cableId)) continue;
      seen.add(cableId);
      out.push({
        cableId,
        typOk: cable.typOk,
        fiberCount: cable.fiberCount,
        sourceRozvadecObjectId: source,
        destRozvadecObjectId: destId,
        destRozvadecName: destName,
      });
    }
  }
  return out.sort((a, b) => {
    const nameCmp = (a.destRozvadecName ?? a.destRozvadecObjectId ?? '').localeCompare(
      b.destRozvadecName ?? b.destRozvadecObjectId ?? '',
      'cs',
    );
    if (nameCmp !== 0) return nameCmp;
    return a.cableId.localeCompare(b.cableId);
  });
}

/** One typOk per OK material row, repeated by quantity (2× 300218 → two 24 vl.). */
export function typOkListFromWeldMaterials(
  materials?: readonly OkMaterialUsageLike[] | null,
): DerivedTypOkValue[] {
  if (!materials?.length) return [];
  const out: DerivedTypOkValue[] = [];
  for (const row of materials) {
    const typOk = typOkFromMaterial(row);
    if (!typOk) continue;
    const rawQty = row.quantity;
    const qty = rawQty == null ? 1 : Number(rawQty);
    const copies = Number.isFinite(qty) && qty > 0 ? Math.min(Math.floor(qty), 12) : 1;
    for (let i = 0; i < copies; i += 1) out.push(typOk);
  }
  return out;
}

/**
 * Primary OK blown *from* this cabinet (výchozí bod). Used when dest peers are
 * missing — typical OS splice with two origin cables and no buildings.
 * typOk on origin rows is often stripped at záfuk commit; materials fill capacity
 * on those real rows only — never invent placeholder cables from materials.
 */
export function outgoingCablesFromLocalOrigin(
  sourceCabinetId: string,
  originCables: readonly RozvadecWeldCable[],
  materials?: readonly OkMaterialUsageLike[] | null,
): RozvadecWeldLayoutCable[] {
  const source = sourceCabinetId.trim();
  const types = typOkListFromWeldMaterials(materials);
  const rows: Array<{ id: string; typOk: string | null }> = originCables.map((cable) => ({
    id: cable.id.trim(),
    typOk: (cable.typOk ?? '').trim() || null,
  })).filter((row) => row.id);
  if (rows.length === 0) return [];
  if (types.length > 0) {
    const filled = applyDerivedTypOkList(rows.length, types);
    for (let i = 0; i < rows.length; i += 1) {
      if (!rows[i].typOk) rows[i] = { ...rows[i], typOk: filled[i] ?? null };
    }
  }
  const many = rows.length > 1;
  const out: RozvadecWeldLayoutCable[] = [];
  rows.forEach((row, index) => {
    const typOk = row.typOk;
    const fiberCount = fiberCountFromTypOk(typOk);
    const cable: RozvadecWeldLayoutCable = {
      cableId: outgoingWeldCableId(LOCAL_ORIGIN_WELD_DEST_ID, row.id),
      typOk,
      fiberCount,
      sourceRozvadecObjectId: source || null,
      destRozvadecObjectId: null,
      destRozvadecName: many ? `Záfuk ${index + 1}` : 'Primární OK',
    };
    if (fiberCount == null) return;
    out.push(cable);
  });
  return out;
}

/**
 * Peers win when they cover at least as many cables as local origin (stable dest ids).
 * Extra real local origin OK stay listed so an OS splice keeps both sides.
 */
export function resolveOutgoingWeldCables(input: {
  sourceCabinetId: string;
  originCables: readonly RozvadecWeldCable[];
  peerCables: readonly RozvadecWeldLayoutCable[];
  materials?: readonly OkMaterialUsageLike[] | null;
}): RozvadecWeldLayoutCable[] {
  const fromLocal = outgoingCablesFromLocalOrigin(
    input.sourceCabinetId,
    input.originCables,
    input.materials,
  );
  const fromPeers = input.peerCables;
  if (fromPeers.length === 0) return fromLocal;
  if (fromLocal.length <= fromPeers.length) return [...fromPeers];
  // Peers keep stable dest ids (RAY · 24 vl.); leftover real origin OK stay spliceable.
  return [...fromPeers, ...fromLocal.slice(fromPeers.length)];
}
